import * as i0 from '@angular/core';
import { InjectionToken, EventEmitter, LOCALE_ID, Component, Inject, Input, Output, ViewChild, NgModule } from '@angular/core';
import 'ric';
import { formatNumber, CommonModule } from '@angular/common';
import Highcharts from 'highcharts';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { watchResize } from '@slidebean/hydra/resize-observer';
import { ColorSet, Color } from '@slidebean/color';
import * as i1 from '@angular/platform-browser';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import deepMerge from 'deepmerge';
import * as i1$1 from '@slidebean/spreadsheet';
import { factories, INSERT_ROW_BEFORE, INSERT_ROW_AFTER, SEPARATOR, INSERT_COLUMN_BEFORE, INSERT_COLUMN_AFTER, DELETE_ROW, DELETE_COLUMN, TRANSPOSE, SpreadsheetComponent, SpreadsheetModule } from '@slidebean/spreadsheet';

const DEFAULT_CHART_OPTIONS = new InjectionToken('__SB_DEFAULT_CHART_OPTIONS__');

const DEFAULT_CHART_COLORS = [
    '#3ABEFF',
    '#FF3366',
    '#FFE066',
    '#2EC4B6',
    '#9B59B6',
    '#E67E22',
    '#E74C3C',
    '#1ABC9C',
    '#2980B9',
];
function getChartColors(colorSet) {
    const set = new ColorSet(colorSet);
    const backColor = set.backColor;
    const chartColors = Object.keys(colorSet)
        .filter(colorKey => set.getColor(colorKey) !== backColor)
        .sort((a, b) => {
        if (/chart/.test(a) && /chart/.test(b))
            return a < b ? -1 : 1;
        if (/chart/.test(a))
            return -1;
        if (/chart/.test(b))
            return 1;
        return a < b ? -1 : 1;
    })
        .map(colorKey => set.getColor(colorKey));
    // Now darker or lighter
    const alteredColors = chartColors.map(color => {
        return new Color(color).darkenOrLighten(20).val();
    });
    // Add analogous colors
    const analogousColors = [];
    const analogousSets = chartColors.map(color => {
        return new Color(color)
            .analogous()
            .slice(1)
            .map(color => color.val());
    });
    if (analogousSets.length) {
        const analogousSetLength = analogousSets[0].length;
        for (let i = 0; i < analogousSetLength; i++) {
            analogousSets.forEach(analogousSet => {
                analogousColors.push(analogousSet[i]);
            });
        }
    }
    return [...chartColors, ...alteredColors, ...analogousColors];
}

function didChange$1(change) {
    return (change &&
        !change.isFirstChange() &&
        change.previousValue !== change.currentValue);
}
class ChartComponent {
    constructor(domSanitizer, window, locale, defaultOptions) {
        this.domSanitizer = domSanitizer;
        this.window = window;
        this.locale = locale;
        this.stacked = false;
        this.displayDataLabels = false;
        this.displayGridLines = true;
        this.displayTooltips = false;
        this.allowPointerEvents = false;
        this.allowDecimalsXAxis = true;
        this.allowDecimalsYAxis = true;
        this.pieSize = '50%';
        this.pieInnerSize = '0%';
        this.pieDisplayValuesInDataLabels = true;
        this.onSeriesMouseOver = new EventEmitter();
        this._chartData = {
            chart: {
                style: {},
            },
            title: {
                style: {},
            },
            legend: {
                itemStyle: {},
            },
            tooltip: {
                enabled: false,
            },
            xAxis: {
                allowDecimals: true,
                categories: [],
                title: {
                    style: {},
                },
                labels: {
                    style: {},
                },
            },
            yAxis: {
                allowDecimals: true,
                title: {
                    style: {},
                },
                labels: {
                    style: {},
                },
                startOnTick: false,
                endOnTick: false,
                gridLineWidth: 1,
            },
            plotOptions: {
                series: {
                    dataLabels: {
                        enabled: false,
                        style: {},
                    },
                    point: {
                        events: {},
                    },
                },
            },
            series: [],
        };
        this.render$ = new Subject();
        this.destroy$ = new Subject();
        ChartComponent.init(defaultOptions);
    }
    ngOnInit() {
        this.reloadContainerHeight();
        this.reloadContainerTransform();
    }
    ngAfterViewInit() {
        this.render$.pipe(debounceTime(150)).subscribe(this.render.bind(this));
        setTimeout(() => this.render());
        this.window.requestIdleCallback(() => {
            watchResize(this.sizer.nativeElement)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.render$.next(true));
        }, { timeout: 2000 });
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    ngOnChanges(changes) {
        if (this.chartContainer) {
            if (didChange$1(changes['widthToHeightRatio'])) {
                this.reloadContainerHeight();
            }
            if (didChange$1(changes['sizeRatio'])) {
                this.reloadContainerTransform();
            }
            this.render$.next(didChange$1(changes['type']));
        }
    }
    render(force = false) {
        if (!this.chartContainer || !this.chartContainer.nativeElement)
            return;
        this.reloadData();
        this.reloadColors();
        this.reloadFont();
        if (!this._chart || force === true) {
            if (this._chart) {
                this._chart.destroy();
            }
            this._chart = Highcharts.chart(this.chartContainer.nativeElement, this._chartData);
        }
        else {
            this._chart.update(this._chartData, true, true);
        }
    }
    reloadColors() {
        if (this.colorSet) {
            const set = new ColorSet(this.colorSet);
            this._chartData.colors = getChartColors(this.colorSet);
            const textColor = set.textColor;
            const backColor = set.backColor;
            this._chartData.chart.style.color = textColor;
            this._chartData.title.style.color = textColor;
            this._chartData.legend.itemStyle.color = textColor;
            this._chartData.xAxis.title.style.color = textColor;
            this._chartData.xAxis.labels.style.color = textColor;
            this._chartData.yAxis.title.style.color = textColor;
            this._chartData.yAxis.labels.style.color = textColor;
            this._chartData.plotOptions.series.dataLabels.style.color = textColor;
            this._chartData.plotOptions.series.borderColor = backColor;
        }
    }
    reloadFont() {
        if (this.font) {
            this._chartData.chart.style.fontFamily = this.font.fontFamily;
            this._chartData.title.style.fontFamily = this.font.fontFamily;
            this._chartData.xAxis.labels.style.fontFamily = this.font.fontFamily;
            this._chartData.yAxis.labels.style.fontFamily = this.font.fontFamily;
            this._chartData.plotOptions.series.dataLabels.style.fontFamily =
                this.font.fontFamily;
        }
    }
    isPieChart() {
        return this.type === 'pie' || this.type === 'doughnut';
    }
    reloadData() {
        if (this.isPieChart()) {
            this.reloadPieData();
        }
        else {
            this.reloadMultipleSetData();
        }
        // Data labels
        this._chartData.plotOptions.series.dataLabels.enabled =
            this.displayDataLabels;
        // Legend
        const legendPosition = this.legendPosition ?? (this.isPieChart() ? 'off' : 'bottom');
        this._chartData.legend.enabled = legendPosition !== 'off';
        if (legendPosition === 'bottom') {
            this._chartData.legend.align = 'center';
            this._chartData.legend.verticalAlign = 'bottom';
            this._chartData.legend.layout = 'horizontal';
        }
        else if (legendPosition === 'top') {
            this._chartData.legend.align = 'center';
            this._chartData.legend.verticalAlign = 'top';
            this._chartData.legend.layout = 'horizontal';
        }
        else if (legendPosition === 'left') {
            this._chartData.legend.align = 'left';
            this._chartData.legend.verticalAlign = 'middle';
            this._chartData.legend.layout = 'vertical';
        }
        else if (legendPosition === 'right') {
            this._chartData.legend.align = 'right';
            this._chartData.legend.verticalAlign = 'middle';
            this._chartData.legend.layout = 'vertical';
        }
        // Tooltip
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const that = this;
        this._chartData.tooltip.enabled = this.displayTooltips;
        if (this.displayTooltips && !!this.tooltipFormatter) {
            this._chartData.tooltip.formatter = function () {
                return that.tooltipFormatter(this);
            };
        }
        else {
            this._chartData.tooltip.formatter = void 0;
        }
        // Pointer events
        if (this.allowPointerEvents) {
            this._chartData.plotOptions.series.point.events = {
                mouseOver: function () {
                    that.onSeriesMouseOver.emit(this);
                },
            };
        }
        else {
            this._chartData.plotOptions.series.point.events = void 0;
        }
    }
    reloadPieData() {
        this._chartData.chart.type = 'pie';
        this._chartData.series = [];
        if (this.data &&
            this.data.chart &&
            Array.isArray(this.data.chart.labels) &&
            Array.isArray(this.data.chart.values)) {
            this._chartData.title.text = this.data.chart.title || null;
            const serie = {
                showInLegend: true,
                size: this.pieSize,
                innerSize: this.type === 'doughnut' ? '40%' : this.pieInnerSize,
                data: [],
            };
            for (let i = 0; i < this.data.chart.values.length; i++) {
                const value = this.data.chart.values[i];
                let label = '' + this.data.chart.labels[i];
                // Whether to display values or not
                if (this.pieDisplayValuesInDataLabels && label.length) {
                    label += ' - ' + formatNumber(value, this.locale);
                }
                serie.data.push({
                    name: label && label.length > 0 ? label : value,
                    y: value,
                });
            }
            this._chartData.series.push(serie);
        }
    }
    reloadMultipleSetData() {
        this._chartData.chart.type = this.type;
        this._chartData.xAxis.allowDecimals = this.allowDecimalsXAxis;
        this._chartData.yAxis.allowDecimals = this.allowDecimalsYAxis;
        this._chartData.series = [];
        if (this.data &&
            this.data.chart &&
            Array.isArray(this.data.chart.labels) &&
            Array.isArray(this.data.chart.values)) {
            this._chartData.title.text = this.data.chart.title || null;
            // Stacked?
            this._chartData.plotOptions.series.stacking = this.stacked
                ? 'normal'
                : null;
            // X Label
            if (this.data.chart.labelX && this.data.chart.labelX.length) {
                this._chartData.xAxis.title.text = this.data.chart.labelX;
            }
            else {
                this._chartData.xAxis.title.text = '';
            }
            // Y Label
            if (this.data.chart.labelY && this.data.chart.labelY.length) {
                this._chartData.yAxis.title.text = this.data.chart.labelY;
            }
            else {
                this._chartData.yAxis.title.text = '';
            }
            // Y axis scale range
            let minValue = 0;
            let maxValue = 0;
            for (let s = 0; s < this.data.chart.values.length; s++) {
                const valueSet = this.data.chart.values[s];
                const serie = {
                    name: (this.data.chart.sets
                        ? this.data.chart.sets[s] || ''
                        : '').toString(),
                    showInLegend: false,
                    data: [],
                };
                serie.showInLegend = !!serie.name.length;
                for (let i = 0; i < valueSet.length; i++) {
                    const value = valueSet[i];
                    serie.data.push({
                        name: this.data.chart.labels[i],
                        y: value,
                    });
                    if (value < minValue) {
                        minValue = value;
                    }
                    if (value > maxValue) {
                        maxValue = value;
                    }
                }
                if (serie.data.length) {
                    this._chartData.series.push(serie);
                }
            }
            this._chartData.xAxis.categories = Array.from(this.data.chart.labels);
            const hasCustomMax = typeof this.scaleMax === 'number';
            const hasCustomMin = typeof this.scaleMin === 'number';
            const isValidCustomScale = hasCustomMin && hasCustomMax && this.scaleMin < this.scaleMax;
            this._chartData.yAxis.startOnTick = !hasCustomMin;
            this._chartData.yAxis.endOnTick = !hasCustomMax;
            this._chartData.yAxis.floor = null;
            if (isValidCustomScale) {
                this._chartData.yAxis.min = this.scaleMin;
                this._chartData.yAxis.max = this.scaleMax;
            }
            else if (hasCustomMin && !hasCustomMax) {
                this._chartData.yAxis.min = this.scaleMin;
                this._chartData.yAxis.max = null;
            }
            else if (hasCustomMax && !hasCustomMin) {
                this._chartData.yAxis.min = null;
                this._chartData.yAxis.max = this.scaleMax;
            }
            else {
                // Floor
                if (minValue >= 0) {
                    this._chartData.yAxis.floor = 0;
                }
                if (minValue === 0 && maxValue === 0) {
                    // Set max to 5 to show something
                    this._chartData.yAxis.min = 0;
                    this._chartData.yAxis.max = 5;
                }
                else {
                    this._chartData.yAxis.min = null;
                    this._chartData.yAxis.max = null;
                }
            }
            this._chartData.yAxis.tickInterval = this.scaleStep ?? null;
            this._chartData.yAxis.gridLineWidth =
                this.displayGridLines !== false ? 1 : 0;
        }
    }
    reloadContainerHeight() {
        if (this.widthToHeightRatio) {
            this.containerHeight = Math.floor(100 / this.widthToHeightRatio) + '%';
        }
        else {
            this.containerHeight = void 0;
        }
    }
    reloadContainerTransform() {
        const ratio = this.sizeRatio || 0.5;
        const MIN_SCALE = 0.3;
        const MAX_SCALE = 1.7;
        const scale = (MAX_SCALE - MIN_SCALE) * ratio + MIN_SCALE;
        this.containerTransform = this.domSanitizer.bypassSecurityTrustStyle(`scale(${scale})`);
    }
    static { this.isInit = false; }
    static init(defaultOptions) {
        if (ChartComponent.isInit)
            return;
        Highcharts.setOptions(defaultOptions);
        ChartComponent.isInit = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: ChartComponent, deps: [{ token: i1.DomSanitizer }, { token: 'Window' }, { token: LOCALE_ID }, { token: DEFAULT_CHART_OPTIONS }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.10", type: ChartComponent, selector: "sb-chart", inputs: { type: "type", data: "data", colorSet: "colorSet", font: "font", stacked: "stacked", widthToHeightRatio: "widthToHeightRatio", sizeRatio: "sizeRatio", legendPosition: "legendPosition", displayDataLabels: "displayDataLabels", scaleMin: "scaleMin", scaleMax: "scaleMax", scaleStep: "scaleStep", displayGridLines: "displayGridLines", displayTooltips: "displayTooltips", tooltipFormatter: "tooltipFormatter", allowPointerEvents: "allowPointerEvents", allowDecimalsXAxis: "allowDecimalsXAxis", allowDecimalsYAxis: "allowDecimalsYAxis", pieSize: "pieSize", pieInnerSize: "pieInnerSize", pieDisplayValuesInDataLabels: "pieDisplayValuesInDataLabels" }, outputs: { onSeriesMouseOver: "onSeriesMouseOver" }, viewQueries: [{ propertyName: "sizer", first: true, predicate: ["sizer"], descendants: true, static: true }, { propertyName: "chartContainer", first: true, predicate: ["chartContainer"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<div #sizer class=\"sizer\" [style.padding-bottom]=\"containerHeight\">\n  <div\n    #chartContainer\n    class=\"sizer-content\"\n    [style.transform]=\"containerTransform\"\n    [style.pointer-events]=\"allowPointerEvents ? 'auto' : 'none'\"\n  ></div>\n</div>\n", styles: [":host{display:block}.sizer{position:relative;width:100%;height:0;padding-bottom:50%}.sizer .sizer-content{position:absolute;left:0;top:0;width:100%;height:100%;overflow:visible!important}:host ::ng-deep * .highcharts-container{margin:0 auto}:host ::ng-deep * .highcharts-container,:host ::ng-deep * .highcharts-container>svg{overflow:visible!important}:host ::ng-deep * .highcharts-root{font-size:1em!important}:host ::ng-deep * .highcharts-xaxis-labels{transform:translateY(.4em)}:host ::ng-deep * .highcharts-data-labels{opacity:1!important}:host ::ng-deep * .highcharts-data-labels .highcharts-data-label>text{text-shadow:none!important;text-rendering:auto!important}:host ::ng-deep * .highcharts-data-labels .highcharts-data-label>text .highcharts-text-outline{display:none!important}:host ::ng-deep * .highcharts-data-labels.highcharts-pie-series span{min-width:5em!important;max-width:20em!important;white-space:normal!important;word-break:break-all!important;text-overflow:initial!important}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: ChartComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-chart', template: "<div #sizer class=\"sizer\" [style.padding-bottom]=\"containerHeight\">\n  <div\n    #chartContainer\n    class=\"sizer-content\"\n    [style.transform]=\"containerTransform\"\n    [style.pointer-events]=\"allowPointerEvents ? 'auto' : 'none'\"\n  ></div>\n</div>\n", styles: [":host{display:block}.sizer{position:relative;width:100%;height:0;padding-bottom:50%}.sizer .sizer-content{position:absolute;left:0;top:0;width:100%;height:100%;overflow:visible!important}:host ::ng-deep * .highcharts-container{margin:0 auto}:host ::ng-deep * .highcharts-container,:host ::ng-deep * .highcharts-container>svg{overflow:visible!important}:host ::ng-deep * .highcharts-root{font-size:1em!important}:host ::ng-deep * .highcharts-xaxis-labels{transform:translateY(.4em)}:host ::ng-deep * .highcharts-data-labels{opacity:1!important}:host ::ng-deep * .highcharts-data-labels .highcharts-data-label>text{text-shadow:none!important;text-rendering:auto!important}:host ::ng-deep * .highcharts-data-labels .highcharts-data-label>text .highcharts-text-outline{display:none!important}:host ::ng-deep * .highcharts-data-labels.highcharts-pie-series span{min-width:5em!important;max-width:20em!important;white-space:normal!important;word-break:break-all!important;text-overflow:initial!important}\n"] }]
        }], ctorParameters: () => [{ type: i1.DomSanitizer }, { type: Window, decorators: [{
                    type: Inject,
                    args: ['Window']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DEFAULT_CHART_OPTIONS]
                }] }], propDecorators: { type: [{
                type: Input
            }], data: [{
                type: Input
            }], colorSet: [{
                type: Input
            }], font: [{
                type: Input
            }], stacked: [{
                type: Input
            }], widthToHeightRatio: [{
                type: Input
            }], sizeRatio: [{
                type: Input
            }], legendPosition: [{
                type: Input
            }], displayDataLabels: [{
                type: Input
            }], scaleMin: [{
                type: Input
            }], scaleMax: [{
                type: Input
            }], scaleStep: [{
                type: Input
            }], displayGridLines: [{
                type: Input
            }], displayTooltips: [{
                type: Input
            }], tooltipFormatter: [{
                type: Input
            }], allowPointerEvents: [{
                type: Input
            }], allowDecimalsXAxis: [{
                type: Input
            }], allowDecimalsYAxis: [{
                type: Input
            }], pieSize: [{
                type: Input
            }], pieInnerSize: [{
                type: Input
            }], pieDisplayValuesInDataLabels: [{
                type: Input
            }], onSeriesMouseOver: [{
                type: Output
            }], sizer: [{
                type: ViewChild,
                args: ['sizer', { static: true }]
            }], chartContainer: [{
                type: ViewChild,
                args: ['chartContainer', { static: true }]
            }] } });

const CHARTS_DEFAULT_FONT = `'Nunito Sans', 'Helvetica Neue', Arial, sans-serif`;
const CHARTS_DEFAULT_OPTIONS = {
    colors: DEFAULT_CHART_COLORS,
    chart: {
        backgroundColor: 'transparent',
        style: {
            fontFamily: CHARTS_DEFAULT_FONT,
            color: '#555',
            fontSize: '1em',
        },
    },
    title: {
        text: '',
        useHTML: true,
        style: {
            fontSize: '1.2em',
        },
    },
    subtitle: {
        text: '',
    },
    legend: {
        enabled: false,
        useHTML: true,
        itemStyle: {
            fontSize: '0.6em',
            paddingBottom: '0.6em',
        },
    },
    xAxis: {
        startOnTick: false,
        endOnTick: false,
        maxPadding: 0,
        title: {
            style: {
                fontSize: '0.6em',
            },
        },
        labels: {
            style: {
                fontSize: '0.6em',
                lineHeight: '24px',
                fontFamily: CHARTS_DEFAULT_FONT,
                color: '#555',
            },
        },
    },
    yAxis: {
        startOnTick: false,
        endOnTick: false,
        maxPadding: 0,
        title: {
            style: {
                fontSize: '0.6em',
            },
        },
        labels: {
            style: {
                fontSize: '0.6em',
                fontFamily: CHARTS_DEFAULT_FONT,
                color: '#555',
            },
        },
        reversedStacks: false,
    },
    plotOptions: {
        series: {
            dataLabels: {
                enabled: false,
                style: {
                    fontSize: '0.6em',
                    fontFamily: CHARTS_DEFAULT_FONT,
                    color: '#555',
                    fontWeight: 'normal',
                    textShadow: 'none',
                },
            },
        },
        pie: {
            dataLabels: {
                useHTML: true,
            },
            states: {
                hover: {
                    halo: {
                        size: 0,
                    },
                },
            },
        },
    },
    lang: {
        thousandsSep: ',',
        numericSymbols: void 0,
    },
    credits: void 0,
};

function stringToNumber(str) {
    if (typeof str === 'number')
        return str;
    const val = parseFloat((str ?? '').replace(/[^0-9\-\./]/g, ''));
    return isNaN(val) ? null : val;
}
function didChange(change) {
    return (change &&
        !change.isFirstChange() &&
        change.previousValue !== change.currentValue);
}
const disableForLabels = (labelPicker) => (token) => (specs) => {
    const item = factories[token](specs);
    const disabled = item['disabled'];
    item['disabled'] = disabled || labelPicker(specs.target) === 0;
    return item;
};
const disableForXLabels = disableForLabels(({ x }) => x);
const disableForYLabels = disableForLabels(({ y }) => y);
function toSpreadsheetData(sets, labels, values) {
    const data = [];
    data.push(['', ...labels]);
    for (let i = 0; i < values.length; i++) {
        data.push([sets[i], ...values[i]]);
    }
    return data;
}
function toMultisetChartData(data) {
    const sets = [];
    const labels = data.length ? data[0].slice(1) : [];
    const values = [];
    for (let i = 1; i < data.length; i++) {
        const row = data[i];
        if (row.length) {
            sets.push(row[0]);
            values.push(row.slice(1).map(n => stringToNumber(n)));
        }
    }
    return { sets, labels, values };
}
class MultisetChartDataEditorComponent {
    constructor() {
        this.sets = [];
        this.labels = [];
        this.values = [];
        this.onChange = new EventEmitter();
        this.contextMenu = [
            disableForYLabels(INSERT_ROW_BEFORE),
            INSERT_ROW_AFTER,
            SEPARATOR,
            disableForXLabels(INSERT_COLUMN_BEFORE),
            INSERT_COLUMN_AFTER,
            SEPARATOR,
            disableForYLabels(DELETE_ROW),
            disableForXLabels(DELETE_COLUMN),
            SEPARATOR,
            TRANSPOSE,
        ];
    }
    ngOnInit() {
        this.cells = toSpreadsheetData(this.sets, this.labels, this.values);
    }
    ngOnChanges(changes) {
        if (!didChange(changes['sets']) &&
            !didChange(changes['labels']) &&
            !didChange(changes['values']))
            return;
        this.cells = toSpreadsheetData(this.sets, this.labels, this.values);
    }
    flushChanges(cells) {
        const data = toMultisetChartData(cells);
        this.onChange.emit(data);
    }
    transpose() {
        if (!this.spreadsheet)
            return;
        this.spreadsheet.transpose();
    }
    makeFirstCellReadonly({ cell, row, column }) {
        if (row !== 0 || column !== 0)
            return;
        cell.classList.add('readonly');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: MultisetChartDataEditorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.10", type: MultisetChartDataEditorComponent, selector: "sb-multiset-chart-data-editor", inputs: { sets: "sets", labels: "labels", values: "values" }, outputs: { onChange: "onChange" }, viewQueries: [{ propertyName: "spreadsheet", first: true, predicate: SpreadsheetComponent, descendants: true }], usesOnChanges: true, ngImport: i0, template: `
    <sb-spreadsheet
      [cells]="cells"
      [columnWidth]="75"
      [contextMenu]="contextMenu"
      [useWysiwyg]="false"
      [history]="false"
      (onChange)="flushChanges($event)"
      (onCellRender)="makeFirstCellReadonly($event)"
    />
  `, isInline: true, styles: [":host ::ng-deep .jexcel_content tbody tr:nth-child(9n+2)>.jexcel_row{background-color:#52bef2;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+3)>.jexcel_row{background-color:#ff506f;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+4)>.jexcel_row{background-color:#ffe066;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+5)>.jexcel_row{background-color:#64d8c2;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+6)>.jexcel_row{background-color:#9ea8f9;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+7)>.jexcel_row{background-color:#e67e22;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+8)>.jexcel_row{background-color:#e74c3c;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+9)>.jexcel_row{background-color:#1abc9c;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+10)>.jexcel_row{background-color:#2980b9;color:#fff}:host{display:block}sb-spreadsheet{display:block;margin:-1px 0}\n"], dependencies: [{ kind: "component", type: i1$1.SpreadsheetComponent, selector: "sb-spreadsheet", inputs: ["cells", "columns", "useWysiwyg", "columnWidth", "minRows", "minColumns", "minSpareRows", "minSpareColumns", "contextMenu", "scrollTolerance", "history", "allowDeleteColumn", "allowInsertColumn", "allowDeleteRow", "allowInsertRow"], outputs: ["onChange", "onCellRender"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: MultisetChartDataEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-multiset-chart-data-editor', template: `
    <sb-spreadsheet
      [cells]="cells"
      [columnWidth]="75"
      [contextMenu]="contextMenu"
      [useWysiwyg]="false"
      [history]="false"
      (onChange)="flushChanges($event)"
      (onCellRender)="makeFirstCellReadonly($event)"
    />
  `, styles: [":host ::ng-deep .jexcel_content tbody tr:nth-child(9n+2)>.jexcel_row{background-color:#52bef2;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+3)>.jexcel_row{background-color:#ff506f;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+4)>.jexcel_row{background-color:#ffe066;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+5)>.jexcel_row{background-color:#64d8c2;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+6)>.jexcel_row{background-color:#9ea8f9;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+7)>.jexcel_row{background-color:#e67e22;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+8)>.jexcel_row{background-color:#e74c3c;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+9)>.jexcel_row{background-color:#1abc9c;color:#fff}:host ::ng-deep .jexcel_content tbody tr:nth-child(9n+10)>.jexcel_row{background-color:#2980b9;color:#fff}:host{display:block}sb-spreadsheet{display:block;margin:-1px 0}\n"] }]
        }], propDecorators: { spreadsheet: [{
                type: ViewChild,
                args: [SpreadsheetComponent]
            }], sets: [{
                type: Input
            }], labels: [{
                type: Input
            }], values: [{
                type: Input
            }], onChange: [{
                type: Output
            }] } });

const DIRECTIVES = [ChartComponent, MultisetChartDataEditorComponent];
class ChartModule {
    static forRoot(defaultOptions) {
        return {
            ngModule: ChartModule,
            providers: [
                { provide: 'Window', useValue: window },
                {
                    provide: DEFAULT_CHART_OPTIONS,
                    useFactory() {
                        const config = defaultOptions
                            ? deepMerge(CHARTS_DEFAULT_OPTIONS, defaultOptions, {
                                arrayMerge: (_, sourceArray) => sourceArray,
                            })
                            : CHARTS_DEFAULT_OPTIONS;
                        return config;
                    },
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: ChartModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.10", ngImport: i0, type: ChartModule, declarations: [ChartComponent, MultisetChartDataEditorComponent], imports: [CommonModule, SpreadsheetModule], exports: [ChartComponent, MultisetChartDataEditorComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: ChartModule, providers: [provideHttpClient(withInterceptorsFromDi())], imports: [CommonModule, SpreadsheetModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: ChartModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES,
                    exports: DIRECTIVES,
                    imports: [CommonModule, SpreadsheetModule],
                    providers: [provideHttpClient(withInterceptorsFromDi())],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ChartComponent, ChartModule, DEFAULT_CHART_COLORS, DEFAULT_CHART_OPTIONS, MultisetChartDataEditorComponent, getChartColors, toMultisetChartData, toSpreadsheetData };
